import { gettext } from "@/services/translation";
import { bankCodesCZ, bankCodesSK } from "@/services/user/bankInfo/bankInfoConstants";
import { validateIBAN } from "ngx-iban-validator";

export class BankAccountNumberNotValid extends Error {}

/**
 * @throws {BankAccountNumberNotValid}
 */
export function validateDomesticBankAccountNumber(bankAccount: string | null | undefined): asserts bankAccount is string {
  const exception = new BankAccountNumberNotValid(gettext.$gettext("Číslo bankovního účtu není správné"));

  if (typeof bankAccount !== "string") {
    throw exception;
  }

  const parts = bankAccount.split("/");
  if (parts.length !== 2) {
    throw exception;
  }
  const [fullAccountNumber, bankCode] = parts;

  const isCz = bankCodesCZ.includes(bankCode);
  const isSk = bankCodesSK.includes(bankCode);
  if (isCz && isSk) {
    throw new Error("Bank code must be either CZ or SK. Got both. This should never happen. Bank code: " + bankCode);
  }
  if (!isCz && !isSk) {
    throw exception;
  }

  if (isCz) {
    validateCzechAccountNumber(fullAccountNumber);
  } else {
    validateSlovakAccountNumber(fullAccountNumber);
  }
}

/**
 * @throws {BankAccountNumberNotValid}
 */
function validateCzechAccountNumber(number: string): void {
  const exception = new BankAccountNumberNotValid(gettext.$gettext("Číslo bankovního účtu není správné"));

  const parts = number.split("-");
  if (parts.length === 1) {
    const match = number.match(/^(\d{1,10})$/);
    if (!match) {
      throw exception;
    }
    if (!mod11(number, [6, 3, 7, 9, 10, 5, 8, 4, 2, 1])) {
      throw exception;
    }
  } else if (parts.length === 2) {
    const match = number.match(/^(\d{0,6})-(\d{1,10})$/);
    if (!match) {
      throw exception;
    }

    const [, prefix, num] = match;
    if (prefix && !mod11(prefix, [10, 5, 8, 4, 2, 1])) {
      throw exception;
    }
    if (!mod11(num, [6, 3, 7, 9, 10, 5, 8, 4, 2, 1])) {
      throw exception;
    }
  } else {
    throw exception;
  }
}

/**
 * @throws {BankAccountNumberNotValid}
 */
function validateSlovakAccountNumber(number: string): void {
  const exception = new BankAccountNumberNotValid(gettext.$gettext("Číslo bankovního účtu není správné"));
  const accountNumberRegexSK = /^(?:\d{1,6}-)?\d{1,10}$/;
  const match = number.match(accountNumberRegexSK);
  if (!match) {
    throw exception;
  }

  const parts = number.split("-");

  const prefix = parts.length === 2 ? parts[0] : undefined;
  const base = parts.length === 2 ? parts[1] : parts[0];

  if (prefix !== undefined && !mod11(prefix, [10, 5, 8, 4, 2, 1])) {
    throw exception;
  }

  if (!mod11(base, [6, 3, 7, 9, 10, 5, 8, 4, 2, 1])) {
    throw exception;
  }
}

/**
 * Validates only IBAN format - not the real country-specific account number
 *
 * @throws {BankAccountNumberNotValid}
 */
export function validateIban(input: string | null | undefined): void {
  const exception = new BankAccountNumberNotValid(gettext.$gettext("Číslo bankovního účtu není správné"));

  if (typeof input !== "string" || input === "") {
    throw exception;
  }

  const iban = input.replace(/[\p{P}\s]+/gu, "").toUpperCase();

  if (!iban.match(/^[A-Z0-9]{8,}$/)) {
    throw exception;
  }

  const validationResult = validateIBAN(iban);
  const isValid = validationResult === null || validationResult.ibanInvalid === false;
  if (!isValid) {
    throw exception;
  }
}

function mod11(number: string, weights: number[]): boolean {
  const padded = number.padStart(weights.length, "0");
  const sum = padded
    .split("")
    .map((digit, i) => parseInt(digit, 10) * weights[i])
    .reduce((a, b) => a + b, 0);

  return sum % 11 === 0;
}
